import { useState, useCallback, useEffect } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import BrowserCompatibility from 'helpers/browserCompatibility'
import URLParamsHelper from 'helpers/url_params_helper'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'
import HelpVideoLink from 'components/HelpVideoLink'

import AwairRthChart from 'pages/Device/AwairRthPanel/AwairRthChart'
import HeaderControls from 'pages/Device/AwairRthPanel/HeaderControls'
import RotateDeviceMessage from 'pages/shared/RotateDeviceMessage'

import useAwairEvents from 'hooks/useAwairEvents'
import useOrientation from 'hooks/useOrientation'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

const defaultProps = {
  awairDevices: [],
  awairRthDate: null
}

const propTypes = {
  timeZone: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  datesWithData: PropTypes.arrayOf(PropTypes.string).isRequired,
  awairDevices: PropTypes.arrayOf(PropTypes.shape({
    awairDeviceId: PropTypes.number,
    name: PropTypes.string
  })),
  awairRthDate: PropTypes.string
}

const AwairRthPanel = ({ deviceId, datesWithData, awairDevices, timeZone, awairRthDate }) => {
  const [selectedAwairDevice, setSelectedAwairDevice] = useState()

  const [selectedLegendItems, setSelectedLegendItems] = useState({
    temp: true,
    humidity: true,
    co2: true,
    voc: true,
    pm25: true,
    actualAirflow: false
  })
  const [selectedDate, setSelectedDate] = useState((!_.isEmpty(awairRthDate) ? moment(awairRthDate, 'MM-DD-YYYY') : moment()).format('YYYY-MM-DD'))
  const [displayResetZoom, setDisplayResetZoom] = useState(false)
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())

  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)
  const { isPortrait } = useOrientation()

  const { awairEvents, thermostatEvents, displayIndoorUnit, areAwairEventsLoaded, serverErrorOccurred } = useAwairEvents(deviceId, selectedDate, timeZone, refreshTimestamp, selectedAwairDevice)

  const fromTime = moment(selectedDate).startOf('day').format('YYYY-MM-DD HH:mm:ss')
  const toTime = moment(selectedDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')

  useEffect(() => {
    setSelectedAwairDevice(awairDevices.length ? awairDevices[0].awairDeviceId : null)
  }, [deviceId, awairDevices])

  useEffect(() => {
    if (selectedDate) {
      const dateString = moment(selectedDate, 'YYYY-MM-DD').format('MM-DD-YYYY')
      URLParamsHelper.update('awairRthDate', dateString)
    }
  }, [selectedDate])

  const zoomResetCallback = useCallback(() => {
    setDisplayResetZoom(false)
  }, [])

  const refreshCallback = useCallback(() => setRefreshTimestamp(moment().valueOf()), [])

  return (
    <S.Panel id="awair-runtime-history" scrollToSection>
      <S.PanelHeader>
        <S.PanelTitle>Awair Runtime History  <HelpVideoLink videoKey="awair" /></S.PanelTitle>
      </S.PanelHeader>

      {isMobile && isPortrait
        ? (
          <RotateDeviceMessage />
        )
        : (
          <S.Content>
            <HeaderControls
              {...{ displayResetZoom, zoomResetCallback, refreshCallback, datesWithData }}
              dateChangedCallback={setSelectedDate}
              date={selectedDate}
              awairDevicesData={awairDevices}
              awairDeviceSelectionCallback={setSelectedAwairDevice}
            />

            {areAwairEventsLoaded && awairEvents !== null
              ? (
                <AwairRthChart
                  {...{ awairEvents, thermostatEvents, displayIndoorUnit, timeZone, serverErrorOccurred, selectedLegendItems }}
                  date={selectedDate}
                  fromTime={fromTime}
                  toTime={toTime}
                  isZoomed={displayResetZoom}
                  setDisplayResetZoomCallback={setDisplayResetZoom}
                  setSelectedLegendItemsCallback={setSelectedLegendItems}
                />
              )
              : <Spinner variant="page" />
              }
          </S.Content>
        )}
    </S.Panel>
  )
}

AwairRthPanel.defaultProps = defaultProps
AwairRthPanel.propTypes = propTypes

export default AwairRthPanel

const S = {
  Panel: styled(Panel)`
    min-height: 750px;
    background-color: ${color('paneBackground')};
    @media ${screenSize.small} {
      min-height: 0;
    }
  `,
  PanelHeader: styled(Panel.Header)`
    justify-content: flex-start;
  `,
  PanelTitle: styled(Panel.Title)`
  display:flex;
  align-items: center;
  `,
  Content: styled.div`
    padding: 20px;
  `
}
